import { useRef, useState } from 'react';
import { Upload, Loader2, Plus } from 'lucide-react';

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

/**
 * Nahrání souboru rovnou z počítače — výběrem i přetažením.
 *
 * Soubor pošleme přes /api/media, takže skončí i v mediální knihovně (odkazuje
 * se na něj `soubor_id`), ale uživatel do ní kvůli tomu nemusí chodit.
 *
 * onNahrano(soubor, vsechny) — záznamy vrácené API; `soubor` je ten první.
 * varianta: 'zona' = přetahovací plocha, 'dlazdice' = čtvercové políčko 72×72.
 */
export default function NahratMedia({
    onNahrano,
    isDark,
    accept = 'image/*',
    multiple = false,
    slozka = null,
    popisek = 'Přetáhni sem fotku nebo ji vyber z počítače',
    varianta = 'zona',
    accent = '#0CC0DF',
    vyska = 104,
}) {
    const inputRef = useRef(null);
    const [nahravam, setNahravam] = useState(false);
    const [pretazeno, setPretazeno] = useState(false);
    const [chyba, setChyba] = useState('');

    const borderColor = isDark ? 'rgba(255,255,255,0.15)' : 'var(--border-default)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted, #94a3b8)';

    const nahraj = async (fileList) => {
        const soubory = Array.from(fileList || []).filter(Boolean);
        if (!soubory.length) return;
        setNahravam(true);
        setChyba('');
        try {
            const fd = new FormData();
            (multiple ? soubory : [soubory[0]]).forEach(f => fd.append('soubory[]', f));
            if (slozka) fd.append('slozka', slozka);

            // Content-Type schválně nenastavujeme — prohlížeč doplní boundary sám
            const r = await fetch('/api/media', {
                method: 'POST',
                headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: fd,
            });
            if (!r.ok) {
                const e = await r.json().catch(() => ({}));
                throw new Error(e.error || e.message || 'Nahrání se nepovedlo.');
            }
            const data = await r.json();
            const vsechny = Array.isArray(data) ? data : [data];
            if (!vsechny.length) throw new Error('Server nevrátil žádný soubor.');
            await onNahrano(vsechny[0], vsechny);
        } catch (e) {
            setChyba(e.message || 'Nahrání se nepovedlo.');
        }
        setNahravam(false);
        if (inputRef.current) inputRef.current.value = '';
    };

    const vlastnostiPretazeni = {
        onDragOver: (e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setPretazeno(true); } },
        onDragLeave: () => setPretazeno(false),
        onDrop: (e) => {
            if (!e.dataTransfer.files?.length) return;
            e.preventDefault();
            setPretazeno(false);
            nahraj(e.dataTransfer.files);
        },
    };

    const vstup = (
        <input ref={inputRef} type="file" accept={accept} multiple={multiple}
            onChange={e => nahraj(e.target.files)} style={{ display: 'none' }} />
    );

    if (varianta === 'dlazdice') {
        return (
            <>
                {vstup}
                <button onClick={() => inputRef.current?.click()} disabled={nahravam}
                    title={chyba || 'Nahrát z počítače'}
                    className="flex flex-col items-center justify-center rounded-lg"
                    {...vlastnostiPretazeni}
                    style={{
                        width: 72, height: 72, cursor: nahravam ? 'default' : 'pointer', gap: 3,
                        background: pretazeno ? `${accent}15` : 'none',
                        border: `1px dashed ${pretazeno || chyba ? (chyba ? '#EF4444' : accent) : borderColor}`,
                        color: chyba ? '#EF4444' : mutedColor,
                    }}>
                    {nahravam ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                    <span style={{ fontSize: 9, lineHeight: 1.1, textAlign: 'center' }}>
                        {nahravam ? 'Nahrávám…' : 'Z počítače'}
                    </span>
                </button>
            </>
        );
    }

    return (
        <div>
            {vstup}
            <button onClick={() => inputRef.current?.click()} disabled={nahravam}
                className="w-full flex flex-col items-center justify-center rounded-xl"
                {...vlastnostiPretazeni}
                style={{
                    height: vyska, gap: 6, cursor: nahravam ? 'default' : 'pointer',
                    background: pretazeno ? `${accent}12` : (isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)'),
                    border: `1.5px dashed ${pretazeno ? accent : borderColor}`,
                    color: 'var(--text-primary)', padding: '0 12px',
                }}>
                {nahravam
                    ? <Loader2 size={20} className="animate-spin" style={{ color: accent }} />
                    : <Upload size={20} style={{ color: pretazeno ? accent : mutedColor }} />}
                <span style={{ fontSize: 12, fontWeight: 500 }}>
                    {nahravam ? 'Nahrávám…' : popisek}
                </span>
            </button>
            {chyba && <p className="text-[11px] mt-1.5 text-center" style={{ color: '#EF4444' }}>{chyba}</p>}
        </div>
    );
}
